import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { message } from 'antd';
import OptionsBar from './components/OptionsBar';
import TaskSection from './components/TaskSection';
import ChatPanel from './components/ChatPanel';
import ApiKeyModal from './components/ApiKeyModal';
import UploadBox from './components/UploadBox';
import ReferenceGallery from './components/ReferenceGallery';
import MentionPopup from './components/MentionPopup';
import SubjectChips from './components/SubjectChips';
import useTaskManager from './hooks/useTaskManager';
import useSubjects from './hooks/useSubjects';
import { getNextMentionSelection } from './utils/mentionNavigation';
import {
  formatReferenceUploadFailures,
  processReferenceFiles,
} from './utils/referenceUpload';
import { removeSubjectAssets } from './utils/subjectRemoval';
import './styles/creator.css';

const API_KEY_STORAGE = 'creator_api_key';
const DEFAULT_GENERATION_SIZE = '1280x720';

const uniqueUrls = (items) =>
  Array.from(
    new Set((Array.isArray(items) ? items : []).filter((url) => typeof url === 'string' && url))
  );

const isByteDanceVideoModel = (modelId) => {
  if (typeof modelId !== 'string') return false;
  const normalized = modelId.trim().toLowerCase();
  return normalized.startsWith('zeelin-') || normalized.startsWith('doubao-seedance-');
};

const isSeedance25Model = (modelId) => modelId === 'Doubao-Seedance-2.5';

const buildMentionListFromPrompt = (text, subjects) =>
  subjects
    .map((subject) => ({ subject, index: text.indexOf(subject.name) }))
    .filter((item) => item.index !== -1)
    .sort((a, b) => a.index - b.index)
    .map((item) => item.subject);

const buildPromptSegments = (text, subjects) => {
  const mentions = buildMentionListFromPrompt(text, subjects);
  if (!mentions.length) {
    return [{ type: 'text', value: text }];
  }

  const orderedMentions = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const matchMentionAt = (position) =>
    orderedMentions.find((mention) => text.startsWith(mention.name, position));
  const segments = [];
  let cursor = 0;

  while (cursor < text.length) {
    const matched = matchMentionAt(cursor);
    if (matched) {
      segments.push({ type: 'mention', value: matched.name, subject: matched, offset: cursor });
      cursor += matched.name.length;
      continue;
    }

    let nextCursor = cursor + 1;
    while (nextCursor < text.length) {
      const nextMatched = matchMentionAt(nextCursor);
      if (nextMatched) {
        break;
      }
      nextCursor += 1;
    }

    segments.push({ type: 'text', value: text.slice(cursor, nextCursor), offset: cursor });
    cursor = nextCursor;
  }

  return segments;
};


// Find the mention segment that the cursor is adjacent to.
// direction 'backward': cursor is at `pos`, check if pos-1..pos is inside a mention
// direction 'forward':  cursor is at `pos`, check if pos..pos+1 is inside a mention
const findAdjacentMention = (text, pos, subjects, direction) => {
  const segments = buildPromptSegments(text, subjects);
  let offset = 0;
  for (const seg of segments) {
    const segEnd = offset + seg.value.length;
    if (seg.type === 'mention') {
      if (direction === 'backward' && pos > offset && pos <= segEnd) {
        return { start: offset, end: segEnd };
      }
      if (direction === 'forward' && pos >= offset && pos < segEnd) {
        return { start: offset, end: segEnd };
      }
    }
    offset = segEnd;
  }
  return null;
};


const Creator = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [options, setOptions] = useState({});
  const [refMode, setRefMode] = useState('reference');
  const [firstFrameUrl, setFirstFrameUrl] = useState('');
  const [lastFrameUrl, setLastFrameUrl] = useState('');
  const [referenceItems, setReferenceItems] = useState([]);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(API_KEY_STORAGE) || '');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewTask, setPreviewTask] = useState(null);
  const [errorDetailTask, setErrorDetailTask] = useState(null);
  const [gatewayModels, setGatewayModels] = useState({ video: [], image: [], chat: [] });
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [showMention, setShowMention] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCycleState, setMentionCycleState] = useState({});
  const [activeMentionKey, setActiveMentionKey] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [dragUploadProgress, setDragUploadProgress] = useState(0);
  const [isDragUploading, setIsDragUploading] = useState(false);

  const { tasks, addTask, removeTask } = useTaskManager();
  const { subjects, addSubject, removeSubject, replaceSubjects, addUploadSubject } = useSubjects();
  const editorRef = useRef(null);
  const overlayRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const dragCounterRef = useRef(0);

  const placeholderText =
    activeMode === 'chat'
      ? '输入你的问题...'
      : refMode === 'reference'
        ? '上传1-3个参考视频、照片或图片，文字描述、图片URL。支持：风格参考、动作参考、主体参考、场景参考...'
        : '输入文字描述，或者粘贴/上传参考图片...';

  const resolvedPlaceholderText =
    activeMode === 'chat'
      ? '输入你的问题...'
      : refMode === 'reference'
        ? '可上传参考素材：Seedance 2.5 支持图片 30 张、视频 10 个、音频 10 个，其它模型支持图片 9 张、视频 3 个、音频 3 个。'
        : (placeholderText, '输入文字描述，或者粘贴/上传参考图片...');

  const autoResize = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, []);

  const syncMentionState = useCallback((text, caretOffset) => {
    const textBeforeCursor = text.slice(0, caretOffset);
    const atMatch = textBeforeCursor.match(/@([^@\s]*)$/);
    if (atMatch) {
      setShowMention(true);
      setMentionFilter(atMatch[1]);
    } else {
      setShowMention(false);
      setMentionFilter('');
    }
  }, []);

  const applyPromptEdit = useCallback(
    (nextPrompt, nextCaret) => {
      setPrompt(nextPrompt);
      syncMentionState(nextPrompt, nextCaret);
      requestAnimationFrame(() => {
        const ta = editorRef.current;
        if (ta) {
          ta.selectionStart = nextCaret;
          ta.selectionEnd = nextCaret;
          ta.focus();
        }
      });
    },
    [syncMentionState]
  );

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    fetchGatewayModels();
  }, []);

  useEffect(() => {
    if (activeMode && !selectedModel) {
      const models = gatewayModels[activeMode] || [];
      if (models.length > 0) {
        setSelectedModel(models[0]);
      }
    }
  }, [gatewayModels, activeMode, selectedModel]);

  useEffect(() => {
    autoResize();
  }, [prompt, autoResize]);

  useEffect(() => () => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
  }, []);

  const fetchGatewayModels = async () => {
    try {
      const res = await axios.get('/api/pricing/config');
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        const categoryMap = { video: [], image: [], chat: [] };
        const allGroups = [data.token_models, data.image_models, data.video_models];

        for (const group of allGroups) {
          if (!group) continue;
          for (const [id, info] of Object.entries(group)) {
            const item = {
              id,
              name: id,
              description: info.description || '',
              vendor: info.vendor || '',
              category: info.category || '',
            };
            const cat = (info.category || '').toLowerCase();
            if (cat.includes('视频') || cat.includes('video')) {
              categoryMap.video.push(item);
            } else if (cat.includes('图') || cat.includes('image')) {
              categoryMap.image.push(item);
            } else {
              categoryMap.chat.push(item);
            }
          }
        }
        setGatewayModels(categoryMap);
      }
    } catch (err) {
      console.warn('获取模型列表失败:', err.message);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await axios.get('/api/creator/config');
      if (res.data.success) {
        const data = res.data.data;
        setConfig(data);
        if (data.modes && data.modes.length > 0) {
          setActiveMode(data.modes[0].id);
        }
      } else {
        message.error('加载配置失败: ' + res.data.message);
      }
    } catch (error) {
      message.error('加载配置失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const selectFirstModel = (models) => {
    if (models && models.length > 0) {
      setSelectedModel(models[0]);
    } else {
      setSelectedModel(null);
    }
    setOptions({});
    setFirstFrameUrl('');
    setLastFrameUrl('');
    setReferenceItems([]);
  };

  const handleModeChange = useCallback(
    (modeId) => {
      setActiveMode(modeId);
      const models = gatewayModels[modeId] || [];
      selectFirstModel(models);
    },
    [gatewayModels]
  );

  const handleModelChange = useCallback((model) => {
    setSelectedModel(model);
    setOptions({});
  }, []);

  const handleOptionChange = useCallback((key, value) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const getCurrentModels = () => gatewayModels[activeMode] || [];

  const getVideoGenerateMode = () => {
    if (refMode === 'reference') {
      return referenceItems.length > 0 ? 1 : 4;
    }
    if (firstFrameUrl && lastFrameUrl) return 0;
    if (firstFrameUrl) return 2;
    if (lastFrameUrl) return 3;
    return 4;
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      message.warning(activeMode === 'chat' ? '请输入问题' : '请输入描述');
      return;
    }
    if (!selectedModel) {
      message.warning('请选择模型');
      return;
    }
    if (!apiKey) {
      setShowApiKeyModal(true);
      return;
    }

    if (activeMode === 'chat') {
      const userMsg = { role: 'user', content: prompt.trim() };
      setChatMessages((prev) => [...prev, userMsg]);
      setPrompt('');
      setChatLoading(true);

      try {
        const allMessages = [...chatMessages, userMsg].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const res = await axios.post('/api/creator/execute', {
          endpoint: '/v1/chat/completions',
          method: 'POST',
          body: { model: selectedModel.id, messages: allMessages },
          api_key: apiKey,
        });

        if (!res.data.success) {
          throw new Error(res.data.message || 'Request failed');
        }

        const responseBody = res.data.data?.body;
        const reply =
          responseBody?.choices?.[0]?.message?.content || JSON.stringify(responseBody);

        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: reply, model: selectedModel.id },
        ]);
      } catch (err) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: '请求失败: ' + err.message, model: selectedModel.id },
        ]);
      } finally {
        setChatLoading(false);
      }
      return;
    }

    const endpoint =
      activeMode === 'image' ? '/v1/images/generations' : '/v1/video/generations';
    const queryEndpoint = activeMode === 'image' ? undefined : '/v1/video/result';
    const seedance25 = activeMode === 'video' && isSeedance25Model(selectedModel.id);
    const generateMode = activeMode === 'video' ? getVideoGenerateMode() : undefined;
    const normalizedOptions = {
      ...options,
      size: seedance25 ? options.size || '' : options.size || DEFAULT_GENERATION_SIZE,
      duration: seedance25 ? options.duration ?? -1 : options.duration || '5',
    };
    if (seedance25 && (generateMode === 0 || generateMode === 2)) {
      normalizedOptions['byPass.ratio'] = 'adaptive';
    }
    const isByteVideoModel = activeMode === 'video' && isByteDanceVideoModel(selectedModel.id);
    const imageItems = referenceItems.filter((item) => item.mediaType === 'image');
    const videoItems = referenceItems.filter((item) => item.mediaType === 'video');
    const audioItems = referenceItems.filter((item) => item.mediaType === 'audio');
    const imageUrls = uniqueUrls(imageItems.map((item) => item.url));
    const videoUrls = uniqueUrls(videoItems.map((item) => item.url));
    const audioUrls = uniqueUrls(audioItems.map((item) => item.url));
    const legacyReferenceUrls = uniqueUrls(
      referenceItems.filter((item) => item.mediaType !== 'audio').map((item) => item.url)
    );
    setSubmitting(true);
    try {
      await addTask({
        prompt: prompt.trim(),
        model: selectedModel.id,
        options: normalizedOptions,
        apiKey,
        endpoint,
        queryEndpoint,
        mode: activeMode,
        generateMode,
        firstFrameUrl: refMode === 'frame' ? firstFrameUrl || undefined : undefined,
        lastFrameUrl: refMode === 'frame' ? lastFrameUrl || undefined : undefined,
        referenceUrls:
          isByteVideoModel
            ? imageUrls.length > 0
              ? imageUrls
              : undefined
            : legacyReferenceUrls.length > 0
              ? legacyReferenceUrls
              : undefined,
        imageUrls: isByteVideoModel && imageUrls.length > 0 ? imageUrls : undefined,
        videoUrls: isByteVideoModel && videoUrls.length > 0 ? videoUrls : undefined,
        audioUrls: audioUrls.length > 0 ? audioUrls : undefined,
      });
      message.success('任务已提交');
      setPrompt('');
    } catch (err) {
      message.error('提交失败: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApiKeyConfirm = (key) => {
    setApiKey(key);
    localStorage.setItem(API_KEY_STORAGE, key);
    setShowApiKeyModal(false);
  };

  const appendUploadedReferences = useCallback(
    (accepted) => {
      if (!accepted.length) return 0;

      const existingUrls = new Set(referenceItems.map((item) => item.url));
      const nextItems = accepted
        .filter((item) => item?.url && !existingUrls.has(item.url))
        .map((item, index) => {
          const subject = addUploadSubject(item.url, item.file);
          existingUrls.add(item.url);
          return {
            id: `ref-${Date.now()}-${index}`,
            url: item.url,
            name: subject.name,
            ...(item.metadata || {}),
          };
        });

      setReferenceItems((prev) => [...prev, ...nextItems]);
      return nextItems.length;
    },
    [addUploadSubject, referenceItems]
  );

  const removeSubjectFromPrompt = useCallback(
    (subject) => {
      const {
        nextPrompt,
        nextReferenceItems,
        nextFirstFrameUrl,
        nextLastFrameUrl,
        nextSubjects,
      } = removeSubjectAssets({
        subject,
        subjects,
        prompt,
        referenceItems,
        firstFrameUrl,
        lastFrameUrl,
      });
      if (nextSubjects) {
        replaceSubjects(nextSubjects);
      } else {
        removeSubject(subject.id);
      }
      setReferenceItems(nextReferenceItems);
      setFirstFrameUrl(nextFirstFrameUrl);
      setLastFrameUrl(nextLastFrameUrl);
      const caret = editorRef.current?.selectionStart || 0;
      applyPromptEdit(nextPrompt, Math.min(caret, nextPrompt.length));
    },
    [
      applyPromptEdit,
      firstFrameUrl,
      lastFrameUrl,
      prompt,
      referenceItems,
      removeSubject,
      replaceSubjects,
      subjects,
    ]
  );

  const focusMentionOccurrence = useCallback(
    (subject) => {
      const currentCycleIndex = mentionCycleState[subject.id] || 0;
      const nextSelection = getNextMentionSelection(prompt, subject.name, currentCycleIndex);
      if (!nextSelection) {
        message.info('该素材尚未添加到输入内容');
        return;
      }

      setMentionCycleState((prev) => ({
        ...prev,
        [subject.id]: nextSelection.nextCycleIndex,
      }));

      const nextKey = `${subject.id}:${nextSelection.offset}`;
      setActiveMentionKey(nextKey);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = setTimeout(() => {
        setActiveMentionKey('');
      }, 1200);

      requestAnimationFrame(() => {
        const textarea = editorRef.current;
        if (!textarea) return;
        textarea.focus();
        textarea.setSelectionRange(
          nextSelection.offset,
          nextSelection.offset + subject.name.length
        );
        if (overlayRef.current) {
          overlayRef.current.scrollTop = textarea.scrollTop;
        }
      });
    },
    [mentionCycleState, prompt]
  );

  const canDropReferences = activeMode !== 'chat' && refMode === 'reference';

  const handleDragEnter = useCallback(
    (e) => {
      if (!canDropReferences || !e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragCounterRef.current += 1;
      setIsDragActive(true);
    },
    [canDropReferences]
  );

  const handleDragOver = useCallback(
    (e) => {
      if (!canDropReferences || !e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [canDropReferences]
  );

  const handleDragLeave = useCallback(
    (e) => {
      if (!canDropReferences || !e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragActive(false);
      }
    },
    [canDropReferences]
  );

  const handleDrop = useCallback(
    async (e) => {
      if (!canDropReferences || !e.dataTransfer?.files?.length) return;
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragActive(false);
      setIsDragUploading(true);
      setDragUploadProgress(0);

      const files = Array.from(e.dataTransfer.files);
      try {
        const { accepted, failures } = await processReferenceFiles(files, {
          existingItems: referenceItems,
          model: selectedModel?.id,
          onBatchProgress: setDragUploadProgress,
        });

        const uploadedCount = appendUploadedReferences(accepted);
        if (uploadedCount > 0) {
          message.success(`已上传 ${uploadedCount} 个素材`);
        }
        if (failures.length > 0) {
          message.warning(`以下素材未上传：${formatReferenceUploadFailures(failures)}`, 6);
        }
      } catch (error) {
        message.error(`上传失败: ${error.message}`);
      } finally {
        setDragUploadProgress(0);
        setIsDragUploading(false);
      }
    },
    [appendUploadedReferences, canDropReferences, referenceItems, selectedModel]
  );

  const nonChatTasks = tasks.filter((t) => t.mode !== 'chat');
  const processingTasks = nonChatTasks.filter((t) => t.status === 'processing');
  const completedTasks = nonChatTasks.filter((t) => t.status === 'completed');
  const failedTasks = nonChatTasks.filter((t) => t.status === 'failed');
  const recentTasks = [...completedTasks, ...failedTasks].sort(
    (a, b) => (b.completedAt || b.createdAt) - (a.completedAt || a.createdAt)
  );

  if (loading) {
    return (
      <div className="creator-loading">
        <div className="loading-spinner" />
        <div>加载中...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="creator-error">
        <div>配置加载失败</div>
      </div>
    );
  }

  return (
    <>
      <div className="creator-container">
        <div className="creator-main">
          {activeMode === 'chat' ? (
            <ChatPanel messages={chatMessages} loading={chatLoading} />
          ) : (
            <>
              <div className="creator-topbar">
                <div className="creator-topbar-left">
                  <div className="creator-topbar-title">
                    默认创作
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
                <div className="creator-topbar-right">
                  <div className="creator-topbar-search">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                  <div className="creator-topbar-filter">
                    时间
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  <div className="creator-topbar-filter">
                    视频
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  <div className="creator-topbar-filter">
                    操作类型
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="creator-content">
                {processingTasks.length > 0 && (
                  <TaskSection
                    title="当前任务"
                    tasks={processingTasks}
                    onDeleteTask={removeTask}
                    onPreviewTask={setPreviewTask}
                    onOpenErrorTask={setErrorDetailTask}
                  />
                )}

                {recentTasks.length > 0 && (
                  <TaskSection
                    title="最近创作"
                    tasks={recentTasks}
                    onDeleteTask={removeTask}
                    onPreviewTask={setPreviewTask}
                    onOpenErrorTask={setErrorDetailTask}
                  />
                )}

                {nonChatTasks.length === 0 && (
                  <div className="creator-empty-state">
                    <div className="creator-empty-state-text">暂未找到相关内容</div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="creator-bottom-bar">
            <div
              className={`creator-input-card${isDragActive ? ' drag-active' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {(isDragActive || isDragUploading) && (
                <div className="creator-drop-overlay">
                  <div className="creator-drop-title">
                    {isDragUploading ? `正在上传素材 ${dragUploadProgress}%` : '释放以上传参考素材'}
                  </div>
                  <div className="creator-drop-hint">
                    将按图片 1-9、视频 1-3、音频 1-3 的规则逐个校验并上传
                  </div>
                  {isDragUploading && (
                    <div className="creator-drop-progress">
                      <div
                        className="creator-drop-progress-fill"
                        style={{ width: `${dragUploadProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
              {subjects.length > 0 && (
                <SubjectChips
                  subjects={subjects}
                  activeMentionKey={activeMentionKey}
                  onFocusSubject={focusMentionOccurrence}
                  onRemoveSubject={removeSubjectFromPrompt}
                />
              )}

              <div className={`creator-prompt-row${activeMode !== 'chat' && refMode !== 'none' ? ' has-frames' : ''}`}>
                {activeMode !== 'chat' && refMode === 'frame' && (
                  <div className="creator-frame-boxes">
                    <UploadBox
                      label="首帧"
                      title="首帧参考图"
                      value={firstFrameUrl}
                      onChange={setFirstFrameUrl}
                      accept="image/*"
                      onSubjectAdd={(url, file) => addUploadSubject(url, file)}
                    />
                    <UploadBox
                      label="尾帧"
                      title="尾帧参考图"
                      value={lastFrameUrl}
                      onChange={setLastFrameUrl}
                      accept="image/*"
                      onSubjectAdd={(url, file) => addUploadSubject(url, file)}
                    />
                  </div>
                )}

                {activeMode !== 'chat' && refMode === 'reference' && (
                  <ReferenceGallery
                    items={referenceItems}
                    model={selectedModel?.id}
                    compactMode
                    onAdd={(url, file, metadata) => {
                      setReferenceItems((prev) => {
                        if (!url || prev.some((item) => item.url === url)) {
                          return prev;
                        }
                        const subject = addUploadSubject(url, file);
                        return [
                          ...prev,
                          { id: 'ref-' + Date.now(), url, name: subject.name, ...(metadata || {}) },
                        ];
                      });
                    }}
                    onRemove={(id) => {
                      setReferenceItems((prev) => prev.filter((item) => item.id !== id));
                    }}
                  />
                )}

                <div className="creator-prompt-input-wrap">
                  {/* Overlay — renders highlighted mentions */}
                  <div
                    className="prompt-editor-overlay"
                    ref={overlayRef}
                    aria-hidden="true"
                  >
                    {prompt.length === 0
                      ? <span className="prompt-editor-placeholder">{resolvedPlaceholderText}</span>
                      : buildPromptSegments(prompt, subjects).map((seg, i) =>
                          seg.type === 'text'
                            ? <span key={i}>{seg.value}</span>
                            : <span
                                key={i}
                                className={`prompt-mention-highlight${activeMentionKey === `${seg.subject.id}:${seg.offset}` ? ' active' : ''}`}
                              >
                                {seg.value}
                                <span
                                  className="prompt-mention-close"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    const nextPrompt = prompt.slice(0, seg.offset) + prompt.slice(seg.offset + seg.value.length);
                                    const caret = editorRef.current?.selectionStart || 0;
                                    applyPromptEdit(nextPrompt, Math.min(caret, nextPrompt.length));
                                  }}
                                >
                                  &times;
                                </span>
                                <span className="creator-inline-mention-preview">
                                  {seg.subject.imageUrl ? (
                                    <img
                                      className="creator-inline-mention-preview-img"
                                      src={seg.subject.imageUrl}
                                      alt={seg.subject.name}
                                    />
                                  ) : (
                                    <span className="creator-inline-mention-preview-fallback">
                                      {seg.subject.mediaType === 'audio'
                                        ? '音频'
                                        : seg.subject.mediaType === 'video'
                                          ? '视频'
                                          : '主体'}
                                    </span>
                                  )}
                                  <span className="creator-inline-mention-preview-name">
                                    {seg.subject.name}
                                  </span>
                                </span>
                              </span>
                        )
                    }
                  </div>
                  {/* Textarea — actual input, browser-native */}
                  <textarea
                    ref={editorRef}
                    className="prompt-editor-textarea"
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      syncMentionState(e.target.value, e.target.selectionStart);
                    }}
                    onSelect={(e) => syncMentionState(prompt, e.target.selectionStart)}
                    onKeyDown={(e) => {
                      if (e.nativeEvent.isComposing) return;

                      if (e.key === 'Escape' && showMention) {
                        e.preventDefault();
                        setShowMention(false);
                        return;
                      }

                      const ta = editorRef.current;
                      const start = ta.selectionStart;
                      const end = ta.selectionEnd;

                      if (e.key === 'Backspace') {
                        if (start === end && start > 0) {
                          const mention = findAdjacentMention(prompt, start, subjects, 'backward');
                          if (mention) {
                            e.preventDefault();
                            const nextPrompt = prompt.slice(0, mention.start) + prompt.slice(mention.end);
                            applyPromptEdit(nextPrompt, mention.start);
                            return;
                          }
                        }
                        // else: let browser handle natively
                        return;
                      }

                      if (e.key === 'Delete') {
                        if (start === end && end < prompt.length) {
                          const mention = findAdjacentMention(prompt, end, subjects, 'forward');
                          if (mention) {
                            e.preventDefault();
                            const nextPrompt = prompt.slice(0, mention.start) + prompt.slice(mention.end);
                            applyPromptEdit(nextPrompt, mention.start);
                            return;
                          }
                        }
                        return;
                      }

                      if (e.key === 'Enter' && !e.shiftKey && !showMention) {
                        e.preventDefault();
                        handleSubmit();
                        return;
                      }
                    }}
                    onScroll={(e) => {
                      if (overlayRef.current) {
                        overlayRef.current.scrollTop = e.target.scrollTop;
                      }
                    }}
                    rows={1}
                  />
                </div>
              </div>

              {showMention && (
                <MentionPopup
                  subjects={subjects}
                  filter={mentionFilter}
                  onDelete={(id) => {
                    const target = subjects.find((s) => s.id === id);
                    if (!target) return;
                    removeSubjectFromPrompt(target);
                  }}
                  onSelect={(subject) => {
                    const cursor = editorRef.current?.selectionStart || 0;
                    const before = prompt.slice(0, cursor);
                    const after = prompt.slice(cursor);
                    const cleaned = before.replace(/@[^@\s]*$/, '');
                    const nextPrompt = `${cleaned}${subject.name}${after}`;
                    const nextCaret = cleaned.length + subject.name.length;
                    setShowMention(false);
                    setMentionFilter('');
                    applyPromptEdit(nextPrompt, nextCaret);
                  }}
                  onCreate={(subject) => {
                    addSubject(subject);
                    const cursor = editorRef.current?.selectionStart || 0;
                    const before = prompt.slice(0, cursor);
                    const after = prompt.slice(cursor);
                    const cleaned = before.replace(/@[^@\s]*$/, '');
                    const nextPrompt = `${cleaned}${subject.name}${after}`;
                    const nextCaret = cleaned.length + subject.name.length;
                    setShowMention(false);
                    setMentionFilter('');
                    applyPromptEdit(nextPrompt, nextCaret);
                  }}
                  onClose={() => {
                    setShowMention(false);
                    setMentionFilter('');
                  }}
                />
              )}

              <OptionsBar
                modes={config.modes || []}
                activeMode={activeMode}
                onModeChange={handleModeChange}
                models={getCurrentModels()}
                selectedModel={selectedModel}
                onModelChange={handleModelChange}
                options={options}
                onOptionChange={handleOptionChange}
                refMode={refMode}
                onRefModeChange={(mode) => {
                  setRefMode(mode);
                  setFirstFrameUrl('');
                  setLastFrameUrl('');
                  setReferenceItems([]);
                }}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            </div>
          </div>
        </div>
      </div>

      {showApiKeyModal && (
        <ApiKeyModal
          initialKey={apiKey}
          onConfirm={handleApiKeyConfirm}
          onCancel={() => setShowApiKeyModal(false)}
        />
      )}

      {previewTask && (previewTask.videoUrl || previewTask.imageUrl) && (
        <div className="creator-preview-overlay" onClick={() => setPreviewTask(null)}>
          <div className="creator-preview-content" onClick={(e) => e.stopPropagation()}>
            {previewTask.mode === 'image' ? (
              <img src={previewTask.imageUrl || previewTask.videoUrl} alt={previewTask.prompt} />
            ) : (
              <video src={previewTask.videoUrl || previewTask.imageUrl} controls autoPlay />
            )}
          </div>
          <button className="creator-preview-close" onClick={() => setPreviewTask(null)}>
            &times;
          </button>
        </div>
      )}

      {errorDetailTask && (
        <div className="creator-preview-overlay" onClick={() => setErrorDetailTask(null)}>
          <div className="creator-error-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="creator-error-detail-title">任务错误信息</div>
            <div className="creator-error-detail-summary">
              {errorDetailTask.error || '任务失败'}
            </div>
            <pre className="creator-error-detail-body">
              {errorDetailTask.errorDetail || errorDetailTask.error || ''}
            </pre>
          </div>
          <button className="creator-preview-close" onClick={() => setErrorDetailTask(null)}>
            &times;
          </button>
        </div>
      )}
    </>
  );
};

export default Creator;
